import type { ReactNode } from "react";
import { Building2, FolderOpen } from "lucide-react";
import { NavLink } from "react-router-dom";

import { cn } from "@/lib/utils";

export interface BuildingContextLineProps {
  /** The current building's name, shown in medium weight. */
  name: string;
  /**
   * Building dashboard destination. Omit to render the crumb as plain text —
   * see {@link ContextCrumb}.
   */
  buildingHref?: string | null;
  /**
   * The selected project, shown as read-only context after the building name.
   * A node rather than a string so a caller that knows the id but not yet the
   * name (a `?project=` deep link, options still loading) can hold the crumb's
   * place with a skeleton instead of letting it pop in after the paint.
   */
  projectName?: ReactNode;
  /** Selected project's dashboard destination. Omit to render plain text. */
  projectHref?: string | null;
  /**
   * Optional control rendered against the project crumb — the
   * ProjectScopeSwitcher's chevron. Kept a slot so this component stays pure
   * presentation and the caller owns the scope side effects. Dropped along
   * with the crumb when no project is selected.
   */
  projectMenu?: ReactNode;
  /** The current building's address, shown muted after a dot separator. Hidden when empty. */
  address?: string | null;
  /** Localised accessible name for the building/project breadcrumb. */
  breadcrumbLabel: string;
  /**
   * Outer wrapper classes. Defaults to the standalone `px-6 pt-4` gutter; pass
   * an override (e.g. `""`) when composing the line into an already-padded row.
   */
  className?: string;
}

/**
 * One crumb — a link to its dashboard, or plain text when no destination is
 * given. Both dashboards sit behind `SubcontractorRedirect`, so for a
 * subcontractor-only user a link is guaranteed to bounce them to Files: they
 * get the same context, without an affordance that never does what it says.
 */
function ContextCrumb({
  href,
  icon: Icon,
  label,
  className,
}: {
  href?: string | null;
  icon: typeof Building2;
  label: ReactNode;
  className: string;
}) {
  const content = (
    <>
      <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0 truncate">{label}</span>
    </>
  );
  const base = cn("flex min-w-0 items-center font-medium text-foreground", className);

  return href ? (
    <NavLink to={href} end className={cn(base, "hover:text-foreground/80")}>
      {content}
    </NavLink>
  ) : (
    <span className={base}>{content}</span>
  );
}

/**
 * A breadcrumb-style context row that identifies which building you are in,
 * shown at the very top of the content area on every building page — above
 * the page title (Dashboard, Documents, Documents Log, Projects, Issues, …).
 *
 * Rendered once by {@link BuildingLayout} above the routed page, so it stays
 * consistent across every tab and never touches the navigation menu (which is
 * a sibling sidebar). A single, understated left-aligned line, unboxed:
 * building icon + building name (medium weight) / selected project (when
 * provided) · address (muted). Both names are links to their dashboards; a
 * crumb whose href is omitted is plain text instead. Switching project is the
 * `projectMenu` slot's job (the chevron beside the project name) — the name
 * itself only ever navigates. The address is optional and drops out — along
 * with its leading separator — when absent.
 *
 * Renders nothing until the building name is available, so it never flashes an
 * empty "· " row while the building detail is still loading.
 */
export function BuildingContextLine({
  name,
  buildingHref,
  projectName,
  projectHref,
  projectMenu,
  address,
  breadcrumbLabel,
  className = "px-6 pt-4",
}: BuildingContextLineProps) {
  if (!name) return null;

  return (
    <div className={cn(className)}>
      <div className="flex min-w-0 max-w-full items-center gap-2 text-sm">
        <nav aria-label={breadcrumbLabel} className="min-w-0">
          <ol className="flex min-w-0 items-center gap-2">
            <li className="flex min-w-0">
              <ContextCrumb href={buildingHref} icon={Building2} label={name} className="gap-2" />
            </li>
            {projectName ? (
              <>
                <li className="flex-shrink-0 text-muted-foreground/60" aria-hidden="true">
                  /
                </li>
                <li className="flex min-w-0 items-center gap-1">
                  <ContextCrumb
                    href={projectHref}
                    icon={FolderOpen}
                    label={projectName}
                    className="gap-1.5"
                  />
                  {projectMenu}
                </li>
              </>
            ) : null}
          </ol>
        </nav>
        {address ? (
          <>
            <span className="flex-shrink-0 text-muted-foreground" aria-hidden="true">
              &middot;
            </span>
            <span className="min-w-0 truncate text-muted-foreground">{address}</span>
          </>
        ) : null}
      </div>
    </div>
  );
}
